import { ApplianceUpdateGate } from "./components/ApplianceUpdateGate";
import { EnvironmentGate } from "./components/EnvironmentSettings";
import { CommercialIdentityGate } from "./components/CommercialIdentityGate";
import { SystemDataGate } from "./components/SystemDataSettings";
import { RefreshCcw } from "lucide-react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "./lib/api";
import { Shell } from "./components/Shell";
import { ProspectsPage } from "./pages/ProspectsPage";
import { UsersPage } from "./pages/UsersPage";
import { MeetingTeamPage } from "./pages/MeetingTeamPage";
import { SystemPage } from "./pages/SystemPage";
import { CommercialDashboardPage } from "./pages/CommercialDashboardPage";
import { CloserInboxPage } from "./pages/CloserInboxPage";
import { BrandLogo } from "./components/BrandLogo";
import {
  AgentsPage,
  ApprovalsPage,
  CampaignDetailPage,
  CampaignsPage,
  IntegrationsPage,
  LoginPage,
  OperationPage,
  OfferDetailPage,
  OffersPage,
} from "./pages/Pages";

export function isApiConnectionError(error: unknown) {
  return error instanceof ApiError && ["API_UNREACHABLE", "API_TIMEOUT"].includes(error.code);
}
export function startupRefetchInterval(error: unknown) {
  return isApiConnectionError(error) ? 10_000 : false;
}

export function StartupFailure({ error, retrying, onRetry }: { error: unknown; retrying: boolean; onRetry: () => void }) {
  const apiError = error instanceof ApiError ? error : null;
  const connectionFailure = isApiConnectionError(error);
  return (
    <main className="startup-failure" aria-labelledby="startup-failure-title">
      <BrandLogo variant="splash" />
      <section role="alert" aria-live="assertive" className="startup-failure-card">
        <span className="eyebrow">Estado de la aplicación</span>
        <h1 id="startup-failure-title">{connectionFailure ? "No pudimos conectar con la API" : "No pudimos iniciar Commercial AI"}</h1>
        <p>{connectionFailure ? "La interfaz está disponible, pero el servicio todavía no responde. Reintentaremos automáticamente sin perder esta página." : "Ocurrió un error al validar la sesión. Podés reintentar o revisar el detalle técnico."}</p>
        <button type="button" className="primary" onClick={onRetry} disabled={retrying}>
          <RefreshCcw size={17} className={retrying ? "spin" : undefined} aria-hidden="true" />
          {retrying ? "Reintentando…" : "Reintentar"}
        </button>
        <details className="technical-disclosure">
          <summary>Ver detalle técnico</summary>
          <dl>
            <div><dt>Código</dt><dd>{apiError?.code ?? "UNEXPECTED_ERROR"}</dd></div>
            <div><dt>HTTP</dt><dd>{apiError?.status || "Sin respuesta"}</dd></div>
            <div><dt>Detalle</dt><dd>{error instanceof Error ? error.message : "Error no identificado"}</dd></div>
          </dl>
        </details>
      </section>
    </main>
  );
}

function Guard() {
  const location = useLocation();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api("/auth/me"),
    retry: false,
    refetchInterval: (query) => startupRefetchInterval(query.state.error),
  });
  if (me.isPending)
    return (
      <div className="splash">
        <BrandLogo variant="splash" />
        <p>Preparando Commercial AI…</p>
      </div>
    );
  if (me.error instanceof ApiError && me.error.status === 401)
    return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />;
  if (me.isError)
    return <StartupFailure error={me.error} retrying={me.isFetching} onRetry={() => void me.refetch()} />;
  return <Shell />;
}

function AdminUsersRoute() {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<{ user: { role: string } }>("/auth/me"),
    retry: false,
  });
  if (me.isPending) return <div className="loading">Validando permisos…</div>;
  if (me.data?.user.role !== "ADMIN") return <Navigate to="/campaigns" replace />;
  return <UsersPage />;
}

function AdminSystemRoute() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me"), retry: false });
  if (me.isPending) return <div className="loading">Validando permisos…</div>;
  if (me.data?.user.role !== "ADMIN") return <Navigate to="/campaigns" replace />;
  return <SystemPage />;
}

function CommercialDashboardRoute() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me"), retry: false });
  if (me.isPending) return <div className="loading">Validando permisos…</div>;
  if (!["ADMIN", "CLOSER_ADMIN"].includes(me.data?.user.role ?? "")) return <Navigate to={me.data?.user.role === "CLOSER_OPERATOR" ? "/closer" : "/campaigns"} replace />;
  return <CommercialDashboardPage />;
}
function CloserRoute() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me"), retry: false });
  if (me.isPending) return <div className="loading">Validando permisos…</div>;
  if (!me.data || !["ADMIN", "CLOSER_ADMIN", "CLOSER_OPERATOR"].includes(me.data.user.role)) return <Navigate to="/campaigns" replace />;
  return <CloserInboxPage />;
}

export function App() {
  return (
    <ApplianceUpdateGate><SystemDataGate><CommercialIdentityGate><EnvironmentGate><Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<Guard />}>
        <Route path="/commercial-dashboard" element={<CommercialDashboardRoute />} />
        <Route path="/closer" element={<CloserRoute />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="/prospects" element={<ProspectsPage />} />
        <Route path="/offers" element={<OffersPage />} />
        <Route path="/offers/:id" element={<OfferDetailPage />} />
        <Route path="/meeting-team" element={<MeetingTeamPage />} />
        <Route path="/operation" element={<OperationPage />} />
        <Route path="/operation/:runId" element={<OperationPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/agents" element={<AgentsPage />} />
        <Route path="/users" element={<AdminUsersRoute />} />
        <Route path="/system" element={<AdminSystemRoute />} />
      </Route>
      <Route path="*" element={<RoleHome />} />
    </Routes></EnvironmentGate></CommercialIdentityGate></SystemDataGate></ApplianceUpdateGate>
  );
}

function RoleHome() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me"), retry: false });
  if (me.isPending) return null;
  return <Navigate to={["CLOSER_ADMIN", "CLOSER_OPERATOR"].includes(me.data?.user.role ?? "") ? "/closer" : "/campaigns"} replace />;
}
